import * as React from 'react'
import { usePaymentsDialog } from '../../components/payments-dialog-wrapper-context'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserEligibleToAutostartQuery } from 'codegen/generated/user'
import { paymentsAutoStartSubscriptionMutationFetcher } from '../../gen/stripe'
import * as Sentry from '@sentry/react'
import { SentryFilterTagAutostartSubscription } from '@repo/sentry-utils/constants'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import { mapCurrencyToAvailableCurrencyCode } from '@repo/cf-user/hooks/use-user-data'
import { SubscriptionPlan } from 'codegen/types'
import { readPaymentModalEntryPoint } from '../../payments-dialog/analytics'
import {
  getAutostartSuccessSubscriptionTypeForUrl,
  getDefaultPlan,
  getStartSubscriptionLoggedOutRedirectPath,
  mapSubscriptionPlanToAmplitudeSubscriptionCategory,
  isCoinPack,
  isStudioAiSubscription,
  isAllAccessSubscription,
} from '../../utils'

export interface StartSubscriptionProps {
  redirectUrl?: string
  openInNewTab?: boolean
  subscriptionPlan: SubscriptionPlan
  promoCode?: string
  isTrial: boolean
}

let autoStartSubscriptionAttempted = false

function formatMutationErrors(
  errors: { code?: string | null; message?: string | null }[] | null | undefined,
) {
  if (!errors?.length) {
    return ''
  }
  return errors.map(e => e.message || e.code || JSON.stringify(e)).join(', ')
}

export async function startSubscription({
  onSuccess,
  onError,
  subscriptionPlan = SubscriptionPlan.AllAccessYearly,
  isTrial = true,
  promoCode,
}: {
  subscriptionPlan?: SubscriptionPlan
  onSuccess?: () => void
  onError?: () => void
  promoCode?: string
  isTrial?: boolean
}) {
  if (autoStartSubscriptionAttempted) {
    return
  }

  let userData = null
  autoStartSubscriptionAttempted = true
  let isEligibleForAutoStartSubscription = false

  const amplitudeSubscriptionCategory =
    mapSubscriptionPlanToAmplitudeSubscriptionCategory(subscriptionPlan)

  try {
    //This is only needed here, no need to fetch it with each page load
    userData = await useUserEligibleToAutostartQuery.fetcher()()
  } catch (error) {
    Sentry.captureException(
      new Error(
        `useUserEligibleToAutostartQuery fetcher failed with error: ${
          (error as Error)?.message ? (error as Error)?.message : (error as string)
        }`,
      ),
      {
        tags: {
          component: SentryFilterTagAutostartSubscription,
        },
      },
    )
  }

  if (isCoinPack(subscriptionPlan)) {
    isEligibleForAutoStartSubscription = isTrial
      ? Boolean(userData?.me?.user?.subscriptionAccess.coins.isEligibleForAutoStartTrial)
      : Boolean(userData?.me?.user?.subscriptionAccess.coins.isEligibleForAutoStart)
  } else if (isStudioAiSubscription(subscriptionPlan)) {
    isEligibleForAutoStartSubscription = isTrial
      ? Boolean(userData?.me?.user?.subscriptionAccess.studioAi.isEligibleForAutoStartTrial)
      : Boolean(userData?.me?.user?.subscriptionAccess.studioAi.isEligibleForAutoStart)
  } else if (isAllAccessSubscription(subscriptionPlan)) {
    isEligibleForAutoStartSubscription = isTrial
      ? Boolean(userData?.me?.user?.subscriptionAccess.allAccess.isEligibleForAutoStartTrial)
      : Boolean(userData?.me?.user?.subscriptionAccess.allAccess.isEligibleForAutoStart)
  }

  const entryPoint = readPaymentModalEntryPoint()

  if (isEligibleForAutoStartSubscription && userData?.me?.user?.stripeCustomer?.id) {
    const currencyCode = userData.me.user.currency.code
    sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_START_CLICKED, {
      subscription_type: amplitudeSubscriptionCategory,
      entry_point: entryPoint,
      promoCode: promoCode || 'none',
    })
    try {
      const result = await paymentsAutoStartSubscriptionMutationFetcher({
        input: {
          currencyCode: mapCurrencyToAvailableCurrencyCode(currencyCode),
          promoCode: promoCode,
          isTrial: isTrial,
          subscriptionPlan: subscriptionPlan,
        },
      })

      const succeeded =
        !result.paymentsAutoStartSubscription.errors?.length &&
        result.paymentsAutoStartSubscription.subscription !== null

      if (succeeded) {
        sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_STARTED_SUCCESS, {
          subscription_type: amplitudeSubscriptionCategory,
          entry_point: entryPoint,
          promoCode: promoCode || 'none',
        })
        onSuccess?.()

        const urlSearchParams = new URLSearchParams()
        urlSearchParams.set('is_subscription_autostart_success', 'true')
        urlSearchParams.set('cf_user_id', userData.me.user.id)
        urlSearchParams.set('cf_currency', String(currencyCode))
        urlSearchParams.set(
          'cf_subscription_type',
          getAutostartSuccessSubscriptionTypeForUrl(subscriptionPlan, isTrial),
        )
        urlSearchParams.set(
          'cf_subscription_id',
          result.paymentsAutoStartSubscription.subscription?.id ?? '',
        )
        urlSearchParams.set('cf_stripe_account', userData.me.user.stripeCustomer?.account)
        autoStartSubscriptionAttempted = false

        setTimeout(() => {
          const redirectUrl = new URL(window.location.href)
          redirectUrl.hash = ''
          for (const [key, value] of urlSearchParams) {
            redirectUrl.searchParams.set(key, value)
          }
          window.location.href = redirectUrl.toString()
        }, 500)

        return
      }

      if (result.paymentsAutoStartSubscription.subscription === null) {
        Sentry.captureException(
          new Error(
            `paymentsAutoStartSubscription mutation failed with errors: ${formatMutationErrors(result.paymentsAutoStartSubscription.errors)}`,
          ),
          {
            tags: {
              component: SentryFilterTagAutostartSubscription,
            },
          },
        )
      }
      if (result.paymentsAutoStartSubscription.subscription?.id === undefined) {
        Sentry.captureException(
          new Error(
            `paymentsAutoStartSubscription mutation did not return a subscription id: ${formatMutationErrors(result.paymentsAutoStartSubscription.errors)}`,
          ),
          {
            tags: {
              component: SentryFilterTagAutostartSubscription,
            },
          },
        )
      }
    } catch (error) {
      Sentry.captureException(
        new Error(
          `paymentsAutoStartSubscription mutation failed with error: ${
            (error as Error)?.message ? (error as Error)?.message : (error as string)
          }`,
        ),
        {
          tags: {
            component: SentryFilterTagAutostartSubscription,
          },
        },
      )
    }
  }

  sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_STARTED_FAILED, {
    subscription_type: amplitudeSubscriptionCategory,
    entry_point: entryPoint,
    promoCode: promoCode || 'none',
  })

  onError?.()
  autoStartSubscriptionAttempted = false
}

export function useStartSubscription({
  subscriptionPlan,
  redirectUrl,
  openInNewTab = false,
  promoCode,
  isTrial = true,
}: StartSubscriptionProps) {
  const [isPending, setIsPending] = React.useState(false)
  const { open } = usePaymentsDialog()
  const { data: isUserLoggedIn } = useIsLoggedInQuery()

  const defaultPlan = getDefaultPlan(subscriptionPlan, isTrial)

  const handleStartSubscription = async () => {
    if (isPending) {
      return
    }
    if (!isUserLoggedIn) {
      let redirectUrlFinal = getStartSubscriptionLoggedOutRedirectPath(subscriptionPlan, isTrial)

      if (redirectUrl) {
        redirectUrlFinal = redirectUrl
      }

      if (!redirectUrlFinal || redirectUrlFinal.length === 0) {
        return
      }

      if (openInNewTab) {
        window.open(redirectUrlFinal, '_blank')
        return
      }

      window.location.href = redirectUrlFinal
      return
    }

    setIsPending(true)

    await startSubscription({
      subscriptionPlan,
      promoCode,
      isTrial,
      onSuccess: () => {
        setIsPending(false)
      },
      onError: () => {
        setIsPending(false)

        if (window.cfOpenPaymentsDialog) {
          window?.cfOpenPaymentsDialog({
            defaultPlan,
          })
        } else {
          open(defaultPlan)
        }
      },
    })
  }

  return { startSubscription: handleStartSubscription, isPending }
}
